// src/components/TagEditor.js
// Reusable multi-select tag editor (Programs library detail panel + Program Builder).
// Pick from existing tags (derived pool passed in as `allTags`) or type to create a
// new one. Up to MAX_TAGS. Colored chips via tagColor. Self-contained styles.
//
// Props:
//   program  : the program object (reads program.tags for initial selection)
//   allTags  : string[] of existing tags across the user's programs (the pool)
//   onClose  : () => void
//   onSave   : (programId, tags[]) => Promise   // persists the tags array

import React, { useState } from 'react';
import { tagColor, tagBg, MAX_TAGS } from '../utils/tagUtils';

function TagEditor({ program, allTags, onClose, onSave }) {
  const [tags, setTags] = useState(Array.isArray(program.tags) ? [...program.tags] : []);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const canAdd = tags.length < MAX_TAGS;
  const q = search.trim();
  const suggestions = allTags.filter(t =>
    !tags.some(x => x.toLowerCase() === t.toLowerCase()) &&
    (q === '' || t.toLowerCase().includes(q.toLowerCase()))
  );
  const exactExists = allTags.some(t => t.toLowerCase() === q.toLowerCase())
    || tags.some(t => t.toLowerCase() === q.toLowerCase());
  const canCreate = q !== '' && !exactExists && canAdd;

  const addTag = (name) => {
    const n = name.trim();
    if (!n) return;
    if (tags.some(t => t.toLowerCase() === n.toLowerCase())) return;
    if (!canAdd) { setError(`Up to ${MAX_TAGS} tags.`); return; }
    setError(null);
    setTags([...tags, n]);
    setSearch('');
  };
  const removeTag = (name) => setTags(tags.filter(t => t !== name));

  const save = async () => {
    setSaving(true); setError(null);
    // Forgiving: if the user typed a tag but didn't click Create/Enter, add it now
    // (as long as it's valid + not a dupe + under the cap), so Save 'just works'.
    let finalTags = tags;
    const pending = search.trim();
    if (pending && canAdd && !tags.some(t => t.toLowerCase() === pending.toLowerCase())) {
      finalTags = [...tags, pending];
      setTags(finalTags);
      setSearch('');
    }
    try {
      await onSave(program.id, finalTags);
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save tags.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Tags</div>
          <button className="modal-close" onClick={() => !saving && onClose()}>×</button>
        </div>

        <div style={S.tagChipRow}>
          {tags.length === 0 && <span style={{ fontSize: 13, color: '#9aa0a8' }}>No tags yet.</span>}
          {tags.map(t => (
            <span key={t} style={{ ...S.tagChip, background: tagBg(t), color: tagColor(t) }}>
              <span style={{ ...S.tagDot, background: tagColor(t) }} />
              {t}
              <span style={S.tagX} onClick={() => removeTag(t)}>×</span>
            </span>
          ))}
        </div>
        <div style={{ fontSize: 12, color: '#9aa0a8', marginBottom: 8 }}>{tags.length}/{MAX_TAGS} tags</div>

        <input
          className="form-input"
          placeholder={canAdd ? 'Search or create a tag…' : `Max ${MAX_TAGS} tags`}
          value={search}
          disabled={!canAdd}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && canCreate) addTag(q); }}
        />

        {canAdd && (q !== '' || suggestions.length > 0) && (
          <div style={S.tagSuggest}>
            {suggestions.map(t => (
              <div key={t} style={S.tagSuggestRow} onClick={() => addTag(t)}>
                <span style={{ ...S.tagDot, background: tagColor(t) }} />
                <span style={{ flex: 1 }}>{t}</span>
              </div>
            ))}
            {canCreate && (
              <div style={S.tagSuggestRow} onClick={() => addTag(q)}>
                <span style={{ ...S.tagDot, background: tagColor(q) }} />
                <span style={{ flex: 1 }}>Create “{q}”</span>
                <span style={{ fontSize: 11, color: '#9aa0a8' }}>new</span>
              </div>
            )}
          </div>
        )}

        {error && <div style={{ color: '#E63946', fontSize: 13, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save Tags'}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  tagChipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6, minHeight: 26 },
  tagChip: { display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '3px 8px', fontSize: 12, fontWeight: 600 },
  tagDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  tagX: { cursor: 'pointer', marginLeft: 2, fontWeight: 700, opacity: 0.7 },
  tagSuggest: { border: '1px solid #e6e8ec', borderRadius: 0, marginTop: 8, maxHeight: 200, overflowY: 'auto' },
  tagSuggestRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 14, borderBottom: '1px solid #f0f1f4' },
};

export default TagEditor;